import { isWithinCzechia } from './bounds.js';
import { sjtskToWgs84 } from './crs.js';
/** Cislo s volitelnym znamenkem; desetinny oddelovac je tecka NEBO carka. */
const NUMBER_SOURCE = '[+-]?\\d+(?:[.,]\\d+)?';
/** `50.200340, 15.176310` / `50,200340 15,176310` — dve cisla oddelena carkou/mezerou. */
const DECIMAL_PAIR = new RegExp(`^(${NUMBER_SOURCE})\\s*[,;\\s]\\s*(${NUMBER_SOURCE})$`, 'u');
/**
 * Jedna souradnice s volitelnou polokouli PRED cislem i ZA cislem
 * (`50.200340N`, `N 50.200340`, `50.200340`).
 *
 * Polokoule ZA cislem nesmi byt oddelena mezerou — jinak by si ji niže
 * ležici slozka vzala pro sebe (`N 50.2 E 15.3` by davalo prvni souradnici
 * polokouli E) a poradi by se otočilo. Mezeru mezi dvema souradnicemi
 * vraci az oddelovac v celem vzoru.
 */
const HEMISPHERE_COMPONENT = '(?:([NSnsEWew])\\s*)?([+-]?\\d+(?:[.,]\\d+)?)([NSnsEWew])?\\s*';
const HEMISPHERE_PAIR = new RegExp(`^${HEMISPHERE_COMPONENT}\\s*[,;\\s]\\s*${HEMISPHERE_COMPONENT}$`, 'u');
/** S-JTSK (JTSK) CR: obe souradnice vetsi cislo, obe záporné. */
const SJTSK_PAIR = /^\s*(-?\d{4,}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{4,}(?:\.\d+)?)\s*$/u;
/**
 * Rozsahy S-JTSK CR s rezervou ~20 km (vypocteno z extrem CR, viz
 * REPORT-step-GPS1.md). Kladne by byly jine systemy (Ferrari, Krovak bez
 * posunu) — ty parsim jako `outOfSjtskRange`, ne jako spatny vysledek.
 */
const SJTSK_X_RANGE = [-920_000, -400_000];
const SJTSK_Y_RANGE = [-1_240_000, -950_000];
/**
 * Od kdy je cislo „tak velke, ze to bude S-JTSK a ne zemepna sirka".
 * Zeměpisná šířka je max 90, takze cokoli vetsi nemuže byt WGS84 — a misto
 * mlhaveho „sirka mimo rozsah" uzivatel dostane upresneni, ze se pokousel o
 * S-JTSK, ktery pro CR nesedi (Ferrari, Starý JTSK, chybnka pri kopirovani).
 */
const SJTSK_MAGNITUDE_MIN = 100_000;
/** Desetiny z cisla s teckou i s desetinnou carkou. */
function toNumber(raw) {
    const value = Number(raw.replace(',', '.'));
    return Number.isFinite(value) ? value : null;
}
function hemisphereSign(value, hemisphere) {
    return hemisphere.toUpperCase() === 'S' || hemisphere.toUpperCase() === 'W'
        ? -Math.abs(value)
        : Math.abs(value);
}
function ok(point, format) {
    const problem = reject(point);
    if (problem !== null)
        return { kind: 'error', code: problem };
    return { kind: 'point', point, format };
}
function bad(code) {
    return { kind: 'error', code };
}
/** Fyzikální rozsah + obalka CR. Vraci kód chyby, nebo `null` kdyz je vse v poradku. */
function reject(point) {
    if (point.lat < -90 || point.lat > 90 || point.lon < -180 || point.lon > 180) {
        return 'outOfRange';
    }
    if (!isWithinCzechia(point))
        return 'outsideCzechia';
    return null;
}
/** Dve cisla tak velka, ze to nemuzou byt WGS84 — uzivatel chtel S-JTSK. */
function looksLikeSjtsk(a, b) {
    return Math.abs(a) >= SJTSK_MAGNITUDE_MIN && Math.abs(b) >= SJTSK_MAGNITUDE_MIN;
}
/** S-JTSK: obe záporné a obe v rozsahu CR. Bez znaménka to neni S-JTSK. */
function isSjtskX(value) {
    return value >= SJTSK_X_RANGE[0] && value <= SJTSK_X_RANGE[1];
}
function isSjtskY(value) {
    return value >= SJTSK_Y_RANGE[0] && value <= SJTSK_Y_RANGE[1];
}
/**
 * Hlavni vstupni bod sdilene knihovny.
 *
 * Vraci `kind: 'none'`, kdyz retezec neni souradnice — to NENI chyba, jen
 * jiny druh vstupu (kdyby to byla chyba, `map-search.ts` by nemohl volat
 * parser na kazde hledani).
 */
export function parseCoordinateInput(raw) {
    const text = raw.trim();
    if (text === '')
        return { kind: 'none' };
    // 3. Polokoule — Drive pred cislem i za nim. Bez aspoi jedne polokoule nejde
    //    o polokoulovy zapis, ale o cisty desetinny bod (viz dalej).
    const hemisphere = HEMISPHERE_PAIR.exec(text);
    if (hemisphere !== null) {
        const hasLetter = [1, 3, 4, 6].some((group) => hemisphere[group] !== undefined);
        if (hasLetter)
            return parseHemisphere(hemisphere);
    }
    // 4. Stupne/minuty/vteriny.
    if (/[°º]/u.test(text))
        return parseDms(text);
    // 5. S-JTSK. Pred desetinnou dvojici, protoze `-708123.45 -1063456.78`
    //    by jinak proselo jako `lat: -708123.45`, coz je mimo fyzikalni rozsah
    //    a uzivateli by ukazalo chybu „sirka mimo 90" misto „byl to S-JTSK bod".
    const sjtsk = SJTSK_PAIR.exec(text);
    if (sjtsk !== null) {
        const first = toNumber(sjtsk[1] ?? '');
        const second = toNumber(sjtsk[2] ?? '');
        if (first === null || second === null)
            return bad('malformed');
        // `1234, 5678` taky vyhovuje „ctyri cisla", ale neni to S-JTSK (kde je
        // vzdy alespon pet cislic) — nech ho projit na desetinny bod.
        if (looksLikeSjtsk(first, second))
            return parseSjtsk(first, second);
    }
    // 1./2. Desetinny bod.
    const decimal = DECIMAL_PAIR.exec(text);
    if (decimal !== null) {
        const lat = toNumber(decimal[1] ?? '');
        const lon = toNumber(decimal[2] ?? '');
        if (lat === null || lon === null)
            return bad('malformed');
        // Dvojice cisel vetsich nez 90 nemuzou byt WGS84 (zeměpisná šířka je max
        // 90) — uzivatel poslal S-JTSK. Bez teto vyhrady by `-708123.45
        // -1063456.78` skoncilo na mlhave „sirka mimo rozsah" misto spravneho bodu.
        if (looksLikeSjtsk(lat, lon))
            return parseSjtsk(lat, lon);
        return ok({ lat, lon }, (decimal[1] ?? '').includes(',') ? 'decimalComma' : 'decimal');
    }
    return { kind: 'none' };
}
function parseSjtsk(first, second) {
    // Poradi Y X (ceska konvence, jak ji pise CUZK i RUIAN) i X Y — rozhodne
    // ROZSAH CR, ne poradi: X a Y se v CR neprekryvaji, takze je poznam, ktery
    // z cisel je X a ktery Y, ati prijde v libovolnem poradi.
    if (isSjtskX(first) && isSjtskY(second)) {
        return ok(sjtskToWgs84({ x: first, y: second }), 'sjtsk');
    }
    if (isSjtskX(second) && isSjtskY(first)) {
        return ok(sjtskToWgs84({ x: second, y: first }), 'sjtsk');
    }
    return bad('outOfSjtskRange');
}
/** Kterou osu urcuje polokoule. `null` = polokoule u cisla neni. */
function axisOf(letter) {
    if (letter === undefined)
        return null;
    return /[NSns]/u.test(letter) ? 'lat' : 'lon';
}
function parseHemisphere(match) {
    const [firstValueRaw, secondValueRaw] = [match[2] ?? '', match[5] ?? ''];
    const firstValue = toNumber(firstValueRaw);
    const secondValue = toNumber(secondValueRaw);
    if (firstValue === null || secondValue === null)
        return bad('malformed');
    const firstLetter = match[3] ?? match[1];
    const secondLetter = match[6] ?? match[4];
    const firstAxis = axisOf(firstLetter);
    const secondAxis = axisOf(secondLetter);
    if (firstAxis !== null && secondAxis !== null && firstAxis === secondAxis)
        return bad('malformed');
    // Bez polokoule je poradi pevne: s irka, delka. S jednou polokouli si druha
    // osa vyplyne z te prvni.
    const firstIsLat = firstAxis === null ? secondAxis !== 'lat' : firstAxis === 'lat';
    const lat = firstIsLat ? firstValue : secondValue;
    const lon = firstIsLat ? secondValue : firstValue;
    const latLetter = firstIsLat ? firstLetter : secondLetter;
    const lonLetter = firstIsLat ? secondLetter : firstLetter;
    return ok({
        lat: latLetter === undefined ? lat : hemisphereSign(lat, latLetter),
        lon: lonLetter === undefined ? lon : hemisphereSign(lon, lonLetter),
    }, 'hemisphere');
}
/**
 * Jedna souradnice ve tvaru `50°12'01.2"N` / `50°12'` / `50°`.
 *
 * Zdroj je BEZ kotvu, aby slo z nej slozit i globalni verze pro sken dvou
 * souradnic v jednom retezci (s kotvami by `matchAll` sel jen od zacatku).
 */
const DMS_PART_SOURCE = '\\s*([+-]?\\d+(?:[.,]\\d+)?)\\s*[°º]' +
    "(?:\\s*(\\d+(?:[.,]\\d+)?)\\s*['\u2032\u2018])?" +
    '(?:\\s*(\\d+(?:[.,]\\d+)?)\\s*["\u2033\u2019])?' +
    '\\s*([NSnsEWew])?\\s*';
const DMS_PART = new RegExp(`^${DMS_PART_SOURCE}$`, 'u');
const DMS_ANY = new RegExp(DMS_PART_SOURCE, 'gu');
function parseDmsPart(raw) {
    const part = DMS_PART.exec(raw);
    if (part === null)
        return null;
    const degrees = toNumber(part[1] ?? '');
    const minutes = part[2] === undefined ? 0 : toNumber(part[2]);
    const seconds = part[3] === undefined ? 0 : toNumber(part[3]);
    if (degrees === null || minutes === null || seconds === null)
        return null;
    if (minutes >= 60 || seconds >= 60)
        return null;
    return {
        value: Math.abs(degrees) + minutes / 60 + seconds / 3600,
        hemisphere: part[4] ?? null,
    };
}
/**
 * Dva DMS cleny oddelene whitespace. Neni to „rozdel na mezerach" — cas
 * `01.2"` obsahuje desetinnou carku, ktera neni oddelovac, a `50° 12' 01"`
 * ma mezery uvnitr cisel. Proto: najdi oba cleny jako celek a zbytek retezce
 * mezi nimi (i na konci) musi byt prazdny.
 */
function scanDms(text) {
    const found = [];
    let consumed = 0;
    for (const match of text.matchAll(DMS_ANY)) {
        const at = match.index;
        // Mezi dvema souradnicemi smi byt jen whitespace a oddelovac.
        if (text.slice(consumed, at).trim() !== '')
            return null;
        const part = parseDmsPart(match[0]);
        if (part === null)
            return null;
        found.push(part);
        consumed = at + match[0].length;
    }
    if (text.slice(consumed).trim() !== '')
        return null;
    return found.length === 2 ? found : null;
}
function parseDms(text) {
    const found = scanDms(text);
    if (found === null)
        return bad('malformed');
    const [first, second] = found;
    // Polokoule urcuje, ktera polovina je sirka a ktera delka — diky tomu
    // funguje i opacne poradi `15°10'34.7"E 50°12'01.2"N`.
    const firstIsLat = first.hemisphere === null ? true : /[NSns]/u.test(first.hemisphere);
    const secondIsLat = second.hemisphere === null ? !firstIsLat : /[NSns]/u.test(second.hemisphere);
    if (firstIsLat === secondIsLat)
        return bad('malformed');
    const lat = firstIsLat ? first : second;
    const lon = firstIsLat ? second : first;
    return ok({
        lat: lat.hemisphere === null ? lat.value : hemisphereSign(lat.value, lat.hemisphere),
        lon: lon.hemisphere === null ? lon.value : hemisphereSign(lon.value, lon.hemisphere),
    }, 'dms');
}
//# sourceMappingURL=coordinates.js.map